import {
  alpha,
  amber,
  attentionAmber,
  blue,
  borderInk,
  brandRed,
  green,
  ocean,
  oceanGray,
  orange,
  red,
  violet,
} from "./palette";

import { resolveDataVizTokens, type DataVizTokens } from "./dataVizPalette";
export type { DataVizKey, DataVizTokens } from "./dataVizPalette";

// dataViz 7키 — **카테고리(데이터 구분) 색**. status가 "상태의 보편 의미"(성공/경고…)를
// 실어 나르는 것과 달리, dataViz는 의미 없는 구분 축이다: 시리즈 A vs B, 왼팔 vs 오른팔,
// 시연 vs 추론 국면처럼 **의미가 데이터에 로컬**인 색 배정에 쓴다(어느 hue가 무엇을 뜻하는지는
// 소비 앱이 정하고, c6는 값·라이트/다크 정합·구분 가능성만 책임진다 — 그래서 키 이름도
// 의미가 아니라 hue다). status 키를 데이터 구분에 유용하면 상태 의미가 오염되고("빨간 밴드 =
// 에러"로 오독), 자유 hex를 열면 앱마다 값이 갈린다 — 그 사이의 층이다. 값은 전부 리저브
// 램프 스텝 파생이며 status 코어와 raw 스텝을 공유할 수 있다(한 raw 값의 다중 intent 선례).
// Selected 알파 사다리는 두지 않는다 — 밴드/시리즈 틴트는 소비처가 자기 공식으로 섞는다
// (예: MediaTimelineBar segment 30%/45%).

// status 코어 14키. 도메인 상태 신호(badge · dot · pill · row rail) 전용 — 컨트롤의
// selected/active 상호작용 축(border.selected* · bg.control.*)과는 별개 어휘다.
// 전 키가 리저브 램프 스텝에서 파생되며(값은 각 테마 블록), 키마다 base + Selected 3단이
// 전개된다 — 선택된 상태 필터/행의 배경용 alpha 사다리로, bg.selected와 같은 테마별
// 알파(라이트 0.20/0.28/0.36 · 다크 0.26/0.34/0.42)를 쓴다.
export type StatusKey =
  | "success"
  | "running"
  | "paused"
  | "error"
  | "warning"
  | "info"
  | "muted"
  | "completed"
  | "ready"
  | "recording"
  | "cancelling"
  | "attention"
  | "exhausted"
  | "checking";

// 한 status 키 집합의 4단 전개형 — base + Selected 3단.
export type StatusStateTokens<K extends string> = {
  readonly [P in K as P | `${P}Selected` | `${P}SelectedHover` | `${P}SelectedActive`]: string;
};

// Layer 2 도메인 그룹 — 도메인 어휘로 코어를 별칭하는 계층. 하나의 상태 대상(subject)은
// 한 그룹이 어휘를 완결 소유한다. 그래서 dataCapture는 수집 세션의 공통 lifecycle과 record
// 확장 상태를 모두 자기 키로 갖는다(별칭은 값 중복이 없어 공짜다). 규칙 전체는 RFC-status-token-layering
// §Layer 2가 소유한다. CSS 변수는 --c6-color-status-<도메인>-<키>(-selected*)로 emit된다.
export type StatusDeviceKey = "healthy" | "degraded" | "error" | "offline" | "checking";
export type StatusJobKey =
  | "pending"
  | "running"
  | "cancelling"
  | "cancelled"
  | "failed"
  | "succeeded"
  | "actionRequired";
export type StatusDataCaptureKey =
  | "off"
  | "loading"
  | "loaded"
  | "running"
  | "paused"
  | "completed"
  | "error"
  | "idle"
  | "recording"
  | "recovery"
  | "done"
  | "committing"
  | "checking";
export type StatusInferenceKey =
  | "loading"
  | "loaded"
  | "running"
  | "paused"
  | "stopping"
  | "completed"
  | "off"
  | "error";
export type StatusCapacityKey = "ample" | "tight" | "scarce" | "exhausted" | "unknown";

export type StatusTokens = StatusStateTokens<StatusKey> & {
  readonly device: StatusStateTokens<StatusDeviceKey>;
  readonly job: StatusStateTokens<StatusJobKey>;
  readonly dataCapture: StatusStateTokens<StatusDataCaptureKey>;
  readonly inference: StatusStateTokens<StatusInferenceKey>;
  readonly capacity: StatusStateTokens<StatusCapacityKey>;
};

type StatusLadder = readonly [number, number, number];

// status 키 집합(키 → 램프 스텝/코어 참조)을 base + Selected 3단으로 전개한다. 알파는 base
// 스텝에서 alpha()로 파생해 파생 관계를 코드에 남긴다(bg.selected*와 같은 규칙).
const statusGroup = <K extends string>(
  defs: Readonly<Record<K, string>>,
  [selected, selectedHover, selectedActive]: StatusLadder,
): StatusStateTokens<K> =>
  Object.fromEntries(
    Object.entries<string>(defs).flatMap(([key, base]) => [
      [key, base],
      [`${key}Selected`, alpha(base, selected)],
      [`${key}SelectedHover`, alpha(base, selectedHover)],
      [`${key}SelectedActive`, alpha(base, selectedActive)],
    ]),
  ) as StatusStateTokens<K>;

// 코어 정의 하나에서 flat 14키 + 도메인 5그룹(38키)을 함께 만든다 — 도메인 키의 값은
// 전부 코어 참조라 코어 값이 바뀌면 도메인 값도 자동으로 따라온다.
const buildStatus = (
  core: Readonly<Record<StatusKey, string>>,
  ladder: StatusLadder,
): StatusTokens => ({
  ...statusGroup<StatusKey>(core, ladder),
  device: statusGroup<StatusDeviceKey>(
    {
      healthy: core.success,
      degraded: core.warning,
      error: core.error,
      offline: core.muted,
      checking: core.checking,
    },
    ladder,
  ),
  job: statusGroup<StatusJobKey>(
    {
      pending: core.paused,
      running: core.running,
      cancelling: core.cancelling,
      cancelled: core.muted,
      failed: core.error,
      succeeded: core.success,
      actionRequired: core.attention,
    },
    ladder,
  ),
  dataCapture: statusGroup<StatusDataCaptureKey>(
    {
      off: core.muted,
      loading: core.paused,
      loaded: core.ready,
      running: core.running,
      paused: core.paused,
      completed: core.completed,
      error: core.error,
      idle: core.ready,
      recording: core.recording,
      recovery: core.attention,
      done: core.completed,
      committing: core.paused,
      checking: core.checking,
    },
    ladder,
  ),
  inference: statusGroup<StatusInferenceKey>(
    {
      loading: core.paused,
      loaded: core.ready,
      running: core.running,
      paused: core.paused,
      stopping: core.cancelling,
      completed: core.completed,
      off: core.muted,
      error: core.error,
    },
    ladder,
  ),
  capacity: statusGroup<StatusCapacityKey>(
    {
      ample: core.success,
      tight: core.warning,
      scarce: core.error,
      exhausted: core.exhausted,
      unknown: core.muted,
    },
    ladder,
  ),
});

export type ColorTokens = {
  readonly bg: {
    readonly canvas: string;
    readonly surface: string;
    readonly surfaceSunken: string;
    readonly surfaceRaised: string;
    readonly surfaceRaisedHigh: string;
    readonly surfaceOverlay: string;
    readonly inverse: string;
    // 상태/역할 면(state layer) — elevation 축이 아니라 콘텐츠 의미 축. solid가 아니라
    // **alpha tint**로 정의한다: solid면 같은 값의 surface 단(예: muted == surfaceRaised) 위에서
    // 사라지지만, alpha는 어떤 surface 위에서든 "한 단계 어둡게/밝게"가 상대 적용된다
    // (M3 state layer / Primer neutral-muted 패턴; RFC-semantic-token-map §solid vs alpha — 면 위에 얹히는
    // 본질이라 scrim/shadow와 같은 alpha 허용군).
    // muted: de-emphasis 컨테이너(배지/칩, text.muted 페어) + 중립 hover.
    readonly muted: string;
    // selected: 목록/옵션의 선택·활성 상태 면. 위에 얹는 표준 텍스트는 text.default.
    // 소비처의 color-mix(primary N%, transparent) 수제 합성의 수렴처.
    readonly selected: string;
    // selected 위 hover — 같은 base에서 alpha 한 단계 상승. 중립 hover(surfaceRaised)로
    // 덮으면 선택 파랑이 끊기므로 같은 계열의 진한 단계를 쓴다(control.primarySubtleHover 선례).
    readonly selectedHover: string;
    // selected 위 active(누름) — hover에서 한 단계 더. border.selected/Hover/Active 3단 축과
    // 대칭(resting < hover < active).
    readonly selectedActive: string;
    // attention: 필터 밖 pinned 행처럼 선택과 독립적으로 주의가 필요한 contextual 면.
    // amber alpha state layer를 rest < hover/selected < active 순서로 사용한다.
    readonly attention: string;
    readonly attentionHover: string;
    readonly attentionActive: string;
    // dangerSurface: red 리저브 기반 알림 카드 면(시트 §Semantic Reserve · Red) — Toast
    // tinted error/warning처럼 내용이 자기 면 위에 앉는 알림 컨테이너 전용. status.* 도트/
    // 스트라이프 어휘와 별개 축이다.
    readonly dangerSurface: string;
    readonly control: {
      readonly primary: string;
      readonly primaryHover: string;
      readonly primaryActive: string;
      // primary의 옅은 컨테이너 톤. selection 밴드(DatePicker range 양 끝 사이) 등 "primary로
      // 강조하되 fill은 아닌" 면에 쓴다(RFC-date-picker). 위에 text.default가 얹혀도 대비 유지.
      readonly primarySubtle: string;
      // primarySubtle 위 hover — 밴드에 속한 셀의 hover 피드백(중립 surface hover로 덮으면
      // range 색이 끊겨 이상해지므로 같은 계열의 한 단계 진한 톤).
      readonly primarySubtleHover: string;
      // primary solid보다 낮고 surface/ghost보다 높은 secondary action tonal fill.
      // 새 hue가 아니라 기존 primarySubtle 계열의 ocean 틴트를 역할별 토큰으로 분리한다.
      readonly secondary: string;
      readonly secondaryHover: string;
      readonly secondaryActive: string;
      // 브랜드/상태 의미가 없는 utility action fill. secondary(action hierarchy)와 별개로
      // neutral color meaning을 표현하며 ghost/surface에서도 accent로 전환되지 않는다.
      readonly neutral: string;
      readonly neutralHover: string;
      readonly neutralActive: string;
      // SigmaKit/training-infra의 Tracking Play 식별자를 보존하는 legacy gradient.
      // alpha stop은 surfaceSunken 위에 합성되는 장식 layer이며 일반 control state fill로
      // 재사용하지 않는다(RFC-semantic-token-map §Background — 인터랙티브 fill).
      readonly trackingPlay: string;
      readonly danger: string;
      readonly dangerHover: string;
      readonly dangerActive: string;
      readonly warning: string;
      readonly warningHover: string;
      readonly warningActive: string;
      readonly disabled: string;
      readonly disabledSunken: string;
    };
  };
  readonly text: {
    readonly muted: string;
    readonly subtle: string;
    readonly default: string;
    // 위계 최상단(subtle < muted < default < strong) — 헤드라인/활성 항목 등 최고 강조.
    // 색만으로 부족하면 weight와 병행한다.
    readonly strong: string;
    readonly disabled: string;
    readonly accent: string;
    readonly warning: string;
    readonly danger: string;
    readonly inverse: string;
    readonly onControl: string;
    readonly onWarningControl: string;
    readonly onDangerControl: string;
    // dangerSurface 면 위 본문 — onControl과 같은 "면 종속 전경" 축. 라이트는 시트 알림
    // 텍스트(deep red), 다크는 딥 레드 면 위 라이트 뉴트럴.
    readonly onDangerSurface: string;
    // status.* 색으로 채운 면 위 잉크(채움형 status 글리프의 기호). status 색은 양 테마 공통의 중간 채도
    // 램프라 잉크도 테마와 무관하게 흰색이다 — 텍스트가 아닌 aria-hidden 글리프 전용.
    readonly onStatus: string;
  };
  readonly border: {
    // 5단계 중립 사다리 (divider < subtle < default < strong < emphasis) — 다크는 배경에
    // 가깝게(저대비), 라이트는 밝게. 값은 oceanGray ramp에서만 파생.
    readonly divider: string;
    readonly subtle: string;
    readonly default: string;
    readonly strong: string;
    // 최고 대비 윤곽 — 경계가 컴포넌트 식별의 유일한 수단인 곳(WCAG 1.4.11 3:1 이상 필요).
    readonly emphasis: string;
    readonly focus: string;
    readonly selected: string;
    readonly selectedHover: string;
    readonly selectedActive: string;
    readonly danger: string;
    // dangerSurface 면 전용 외곽(시트 error border) — 자기 면 위에 앉는 intent라 범용
    // danger 보더의 "전 surface 3:1" 요구 밖. 범용 축은 danger 유지.
    readonly dangerSurface: string;
    readonly disabled: string;
  };
  readonly slider: {
    readonly track: {
      readonly enabled: string;
      readonly disabled: string;
    };
    readonly activeTrack: {
      readonly enabled: string;
      readonly hover: string;
      readonly focused: string;
      readonly pressed: string;
      readonly disabled: string;
    };
    readonly thumb: {
      readonly enabled: string;
      readonly hover: string;
      readonly focused: string;
      readonly pressed: string;
      readonly disabled: string;
    };
    readonly tick: {
      readonly enabled: string;
      readonly active: string;
      readonly disabled: string;
    };
    readonly valueLabel: {
      readonly bg: string;
      readonly text: string;
    };
  };
  // Switch 전용 램프. 공유 사다리에서 빌려 쓰면 표현할 수 없는 두 가지가 분리 사유다:
  // ① disabled에서도 ON/OFF를 면으로 구분해야 하는데 "비활성인데 저채도 유채"인 톤이 공유
  //    토큰에 없다(가장 가까운 `bg.control.primarySubtle`은 컨테이너 intent라 오용이다).
  // ② 트랙 윤곽은 ON/OFF로 바뀌지 않는 잉크 한 색인데, 그 성질을 가진 기존 축
  //    `text.onControl`은 액션 fill 위에서만 대비가 보장되는 "면 종속 전경"이라 OFF 트랙
  //    위에 얹는 계약이 없다.
  // `slider`가 같은 이유(공유 축에 없는 indicator 램프)로 분리된 선례다.
  readonly switch: {
    // OFF 트랙 면 — 무채 oceanGray. transparent가 아니다: 투명이면 disabled OFF와 같은 면이
    // 되어 "꺼짐"과 "못 만짐"이 구분되지 않는다.
    readonly track: {
      readonly enabled: string;
      readonly hover: string;
      readonly pressed: string;
      readonly disabled: string;
    };
    // ON 트랙 면 — 유채 ocean. Checkbox의 checked fill과 같은 극성이다.
    readonly activeTrack: {
      readonly enabled: string;
      readonly hover: string;
      readonly pressed: string;
      readonly disabled: string;
    };
    // 트랙 윤곽 — ON/OFF·hover·pressed 어디서도 바뀌지 않는 잉크라 상태 슬롯이 없다.
    // 켜짐 신호는 윤곽이 아니라 트랙 면과 thumb(위치 + 채도)이 전달한다.
    readonly border: {
      readonly enabled: string;
      readonly disabled: string;
    };
    // OFF thumb — 무채. 트랙과 같은 "채도는 켜짐 신호" 규칙을 잉크에도 적용한다: 유채 잉크가
    // 꺼진 상태에 남아 있으면 OFF가 켜짐과 같은 색으로 읽힌다. disabled는 ON/OFF 공용 비활성
    // 잉크이며, 활성 OFF보다 항상 한 발 물러난다(비활성이 활성보다 또렷한 역전 금지 —
    // §`color.switch.*` 존재감 사다리).
    readonly thumb: {
      readonly enabled: string;
      readonly hover: string;
      readonly pressed: string;
      readonly disabled: string;
    };
    // ON thumb — 유채 ocean. disabled 슬롯이 없는 것은 의도다(비활성 잉크는 `thumb.disabled`).
    readonly activeThumb: {
      readonly enabled: string;
      readonly hover: string;
      readonly pressed: string;
    };
  };
  readonly scrim: {
    readonly default: string;
    // 표준 backdrop dim — 소비처마다 제각각이던 검정 알파(40~45%)를 단일 값으로 통일.
    readonly overlay: string;
  };
  readonly status: StatusTokens;
  readonly dataViz: DataVizTokens;
  // 브랜드 아이덴티티 색 — semantic 신호가 아니라 회사 표기(로고 주변 강조, 브랜드 모먼트)
  // 전용. 값은 양 테마 동일한 램프 밖 독립 base(palette.brandRed)다.
  readonly brand: {
    readonly primary: string;
  };
};

// Elevation = "surface 톤 + 그림자 + (다크) hairline" 세트. 라이트는 그림자가 주 신호,
// 다크는 표면 밝기(bg 사다리)가 주 신호이고 그림자는 약한 ambient, hairline이 가장자리를
// 정의한다. 그림자 어휘는 두 계열로 분리:
// - `shadow.level.1~4` — in-flow 표면(카드/패널, ElevatedSurface elevation 1~4와 1:1).
//   페이지 안에 앉아 있는 면이라 타이트한 지오메트리를 유지하고 단계는 주로 농도로 표현
//   (blur를 크게 키우면 패널이 붕 뜬 것처럼 퍼져 보임).
// - `shadow.overlay` / `shadow.modal` — floating 레이어 전용(각각 popover/dropdown,
//   scrim 동반 다이얼로그). 떠 있는 레이어라 크고 부드러운 그림자가 정당하다.
export type ElevationTokens = {
  // 부상면 1px 윤곽 — 라이트는 transparent(그림자가 경계 역할), 다크는 옅은 화이트 알파.
  // 레이아웃 영향이 없도록 inset box-shadow ring으로 소비한다(ElevatedSurface 참조).
  readonly hairline: string;
  readonly shadow: {
    readonly level: {
      readonly 1: string;
      readonly 2: string;
      readonly 3: string;
      readonly 4: string;
    };
    readonly overlay: string;
    readonly modal: string;
  };
};

export type ShapeTokens = {
  readonly radius: {
    readonly control: string;
    readonly surface: string;
    readonly tooltip: string;
  };
};

export type TypographyTokens = {
  readonly family: {
    readonly body: string;
    readonly mono: string;
  };
  readonly weight: {
    readonly regular: number;
    readonly medium: number;
    readonly semibold: number;
    readonly bold: number;
    readonly extrabold: number;
    readonly action: number;
  };
  readonly size: {
    readonly caption: string;
    readonly body: string;
    readonly title: string;
    readonly head: string;
    readonly headLarge: string;
    readonly headXLarge: string;
  };
  readonly lineHeight: {
    readonly tight: number;
    readonly snug: number;
    readonly normal: number;
  };
};

export const shapeTokens: ShapeTokens = {
  radius: {
    control: "0.23rem",
    surface: "0.25rem",
    tooltip: "0.2rem",
  },
};

export const typographyTokens: TypographyTokens = {
  // family 스택(계약). 메인 엔트리는 폰트 바이너리를 번들하지 않고 이 스택만 노출 — 실제 로드는
  // 소비처가 opt-in `./fonts` 엔트리(CFF woff2 번들, SWD-419) 또는 자체 로드로 한다. Storybook은 dev-only.
  // family명은 번들하는 CFF static face에 맞춰 'Pretendard'(glyf 변수폰트 시절 'Pretendard Variable'에서 정직화).
  // legacy(sigmakit GlobalStyled): body=`'Archivo Condensed', sans-serif`에서 Pretendard로 divergence.
  family: {
    body: "'Pretendard', -apple-system, BlinkMacSystemFont, system-ui, Roboto, 'Helvetica Neue', 'Segoe UI', 'Apple SD Gothic Neo', 'Noto Sans KR', 'Malgun Gothic', sans-serif",
    mono: "source-code-pro, Menlo, Monaco, Consolas, 'Courier New', monospace",
  },
  weight: {
    regular: 400,
    medium: 500,
    semibold: 600,
    bold: 700,
    extrabold: 800,
    action: 700,
  },
  // 역할 기반 타입 스케일(바 스케일 아님) — caption/body/title + head 패밀리(head/headLarge/headXLarge).
  // 값 근거: sigmakit 사용 실측 + dense 엔터프라이즈 표준(Carbon/Ant: caption 12 / body 14 / title 16 / head
  // 20). 상단은 단일 head 패밀리 — 32px도 업계 공통 heading 범위라 `display` 미사용(RFC-semantic-token-map §Typography).
  size: {
    caption: "0.75rem", // 12px — 라벨/메타/dense grid (접근성 floor)
    body: "0.875rem", // 14px — 기본 본문/컨트롤
    title: "1rem", // 16px — 섹션/다이얼로그 타이틀
    head: "1.25rem", // 20px — 페이지 헤딩
    headLarge: "1.5rem", // 24px — 큰 헤딩
    headXLarge: "2rem", // 32px — 최대 헤딩 / 히어로
  },
  lineHeight: {
    tight: 1.25, // head/title
    snug: 1.35, // caption
    normal: 1.5, // body
  },
};

// ── Coastal 액션/시맨틱 축 ──────────────────────────────────────────────────
// Primary fills use separate Coastal anchors per theme. Light darkens from ocean80 through
// ocean90 to ocean100; Dark lightens from ocean130 through ocean125 to ocean120. Solid foregrounds
// stay theme-specific: Light uses ocean155 and Dark uses oceanGray.white. The state ratios are
// 6.15/5.50/4.75 for Light and 6.44/5.54/4.74 for Dark, preserving AA in both themes.
// Outline, link, and slider indicator axes stay separate from fill. ocean80/ocean130 do not reach
// 3:1 non-text contrast on their theme surfaces, so border.focus/selected and Slider retain their
// accessible ramps (Light 120→130→140, Dark 110→100→90). text.accent uses Light ocean135 and
// Dark ocean90 to preserve AA across every IconButton surface state.
// danger/warning fill도 primary와 같은 극성이다 — 라이트는 밝은 fill + near-black 잉크,
// 다크는 어두운 fill + white 잉크. 그래서 전경 세 축이 모두 같은 방향을 보고,
// `onWarningControl`/`onDangerControl`은 잉크 값이 아니라 red/orange fill 위 대비 마진
// 때문에 `onControl`과 분리된 채로 남는다(브랜드 잉크 ocean155는 red 위에서 3:1 아래다).
// 두 축은 밝기가 아니라 hue로 갈린다 — danger는 red 리저브(h≈25), warning은 orange 램프다.
// 두 fill 모두 램프의 채도 정점 쪽으로 올라가 있다(원색 우선). 상태 비율은 라이트 danger
// 6.32/5.53/4.82 · warning 9.89/8.68/7.53, 다크 danger 6.29/5.33/4.54로 여기까지는 AA이고,
// 다크 warning만 3.29/2.89/2.55로 AA 아래다 — 원색 주황(orange40 정점)을 얻는 대가로
// 의도적으로 내준 값이라 cssVariables.test.ts가 이 축만 예외로 잡는다.
// fill의 면 대비는 3:1을 게이트로 두지 않는다 — primary fill이 이미 그 아래이고, 라벨이
// 있는 solid 컨트롤은 면 경계가 유일한 식별 수단이 아니다.

// Elevation 사다리(양 테마 공통 축): surfaceSunken < canvas < surface < surfaceRaised
// < surfaceRaisedHigh < surfaceOverlay. 다크는 위로 갈수록 밝아지고(표면 밝기 = 광원과의
// 거리), 라이트는 surface(white)를 기준으로 위 단계가 점차 톤 다운되며 그림자가 주 신호.
export const lightColorTokens: ColorTokens = {
  bg: {
    canvas: oceanGray.gray10,
    surface: oceanGray.white,
    surfaceSunken: oceanGray.gray20,
    surfaceRaised: oceanGray.gray30,
    surfaceRaisedHigh: oceanGray.gray40,
    surfaceOverlay: oceanGray.gray50,
    inverse: oceanGray.gray160,
    // base는 gray190(#090c14) — surface(white) 위에서 ≈ #ebebec, raised 위에서도 한 단계 더 어둡게.
    muted: alpha(oceanGray.gray190, 0.08),
    // base는 브랜드 앵커 ocean110 — 양 테마가 같은 스텝에서 선택 틴트를 파생한다.
    // surface(white) 위에서 ≈ rgb(209 231 236), 위 text.accent 6.04:1.
    selected: alpha(ocean.ocean110, 0.2),
    // surface(white) 위에서 ≈ rgb(191 221 229).
    selectedHover: alpha(ocean.ocean110, 0.28),
    // surface(white) 위에서 ≈ rgb(172 211 221).
    selectedActive: alpha(ocean.ocean110, 0.36),
    attention: alpha(attentionAmber.light, 0.16),
    attentionHover: alpha(attentionAmber.light, 0.24),
    attentionActive: alpha(attentionAmber.light, 0.32),
    // 시트 라이트 알림 면(#fbe9e9) — 위에 onDangerSurface(red120)가 6.62:1로 얹힌다.
    dangerSurface: red.red5,
    control: {
      primary: ocean.ocean80,
      primaryHover: ocean.ocean90,
      primaryActive: ocean.ocean100,
      // 옅은 컨테이너 톤 — 파생 근거는 ocean 램프 주석.
      primarySubtle: ocean.ocean30,
      // ocean 램프의 한 단 진한 틴트.
      primarySubtleHover: ocean.ocean40,
      // secondary는 bg 사다리(L* 85.7~100) 밖 독립 tonal 축이다 — 면 대역에 겹치면
      // surfaceRaised와 밝기가 같아져(대비 1.00) 면에 묻힌다. primary(L* 72.8)와 면 대역 사이
      // 빈 구간에 rest L* 82를 두고 hover/active가 primary 쪽으로 2.5씩 진행한다.
      secondary: ocean.ocean60,
      secondaryHover: ocean.ocean65,
      secondaryActive: ocean.ocean70,
      // utility action은 oceanGray ramp를 한 단계씩 진행한다(rest < hover < active).
      neutral: oceanGray.gray40,
      neutralHover: oceanGray.gray50,
      neutralActive: oceanGray.gray60,
      trackingPlay:
        "conic-gradient(#ff5f6dba, #ffc371ba, #ffee55ba, #6be675ba, #4fd0ffba, #4778e2ba, #9a5bffba, #ff6bd6ba, #ff5f6dba)",
      // red/orange fill도 primary와 같은 방향으로 어두워진다(rest < hover < active).
      danger: red.red50,
      dangerHover: red.red70,
      dangerActive: red.red72,
      warning: orange.orange20,
      warningHover: orange.orange30,
      warningActive: orange.orange40,
      // surface 톤 차용 관계 유지: disabled = surfaceRaisedHigh, disabledSunken = surfaceSunken
      disabled: oceanGray.gray40,
      disabledSunken: oceanGray.gray20,
    },
  },
  text: {
    // muted/subtle 모두 slate 계열 — 대비 근거는 oceanGray gray136/gray133 스텝 주석.
    muted: oceanGray.gray136,
    subtle: oceanGray.gray133,
    default: oceanGray.gray180,
    strong: oceanGray.gray210,
    // default의 α0.38 파생.
    disabled: alpha(oceanGray.gray180, 0.38),
    accent: ocean.ocean135,
    warning: orange.orange150,
    danger: red.red120,
    inverse: oceanGray.gray20,
    onControl: ocean.ocean155,
    // red/orange fill은 primary와 같은 극성이지만 유채 잉크(ocean155)로는 대비가 안 나온다 —
    // 같은 방향의 중립 잉크(text.default 단)를 쓴다.
    onWarningControl: oceanGray.gray180,
    onDangerControl: oceanGray.gray180,
    onDangerSurface: red.red120,
    onStatus: oceanGray.white,
  },
  border: {
    // 정적 5단은 면 위에 얹는 검정 alpha 오버레이다(수치와 소비처는 Storybook
    // `Foundations/Theme/Border Ladder` 참조). 고정색이면 같은 토큰이 면마다 1.00~1.70으로
    // 요동쳐 어떤 면에서는 단이 정확히 사라진다 — 면이 6단인데 보더가 면에 종속되지 않기
    // 때문이다. alpha는 면과 함께 움직이므로 요동이 1.01~1.05배 안에 들어오고, 어느 면에서도
    // 사다리 순서가 뒤집히지 않는다.
    // rest 3단은 조용하게 둔다 — 경계가 진하면 화면이 "테두리 상자"로 읽힌다. 대신 인접
    // ΔL* 7.3/10.3으로 서로는 구분된다.
    // divider: 내부 구분선(리스트 행, 섹션, header/footer) — 모든 면에서 1.36~1.38:1.
    divider: alpha(borderInk.light, 0.14),
    // subtle: 컨테이너/패널 기본 윤곽(Modal dialog 박스, Card) + 입력류 rest — 1.65~1.69:1.
    // legacy `outlineVariant`.
    subtle: alpha(borderInk.light, 0.22),
    // default: 콘텐츠보다 도드라지지 않는 rest 윤곽(그리드·카드류) — 2.20~2.30:1.
    // 경계가 유일한 식별 수단인 입력류는 rest에 subtle을 쓰고 hover에서 strong으로 올린다.
    default: alpha(borderInk.light, 0.33),
    // strong: hover·강조 윤곽 — 3.61~3.98:1로 모든 면에서 WCAG 1.4.11의 3:1을 지킨다.
    strong: alpha(borderInk.light, 0.5),
    // emphasis: 최고 대비 윤곽(경계가 유일한 식별 수단) — 5.31~6.20:1.
    emphasis: alpha(borderInk.light, 0.62),
    // 상호작용 외곽 4단은 ocean 유채 축이다. 정적 5단과는 채도로 가른다 — 합성된 정적 단은
    // 최대 19%, 이 축은 80~100%다. 밝기로는 가를 수 없다: 정적 5단이 면 6단을 가로지르므로
    // 어느 면에서든 한 단은 이 축과 밝기가 스친다.
    // focus는 selected와 값이 다르다 — 같은 값이면 열린 Dropdown 이나 눌린 ColorPicker
    // 스와치처럼 이미 선택된 대상에 포커스가 왔을 때 외곽이 변하지 않는다.
    focus: ocean.ocean140,
    selected: ocean.ocean120,
    selectedHover: ocean.ocean125,
    selectedActive: ocean.ocean130,
    // 모든 라이트 surface(overlay 포함) 위 3:1을 지키는 bright red. 상호작용 축이 유채로
    // 옮겨가면서 hue 거리가 157°로 벌어져, 오류 외곽이 선택 외곽과 색으로 갈린다.
    danger: red.red80,
    // fill 곡선 위 단(#f40026) — dangerSurface 면(#fbe9e9) 위 3.68:1.
    dangerSurface: red.red75,
    // disabled는 상태색이 아닌 정적 윤곽이라 유채 상호작용 축을 따라가지 않고 oceanGray에서
    // 나온다. 자기 자리 면 위 1.28~1.72:1로 보이되 enabled rest(default 2.30:1)보다 약하다 —
    // "비활성인데 강조" 역전을 피한다.
    disabled: oceanGray.gray60,
  },
  slider: {
    track: {
      enabled: oceanGray.gray50,
      disabled: oceanGray.gray40,
    },
    activeTrack: {
      enabled: ocean.ocean120,
      hover: ocean.ocean130,
      focused: ocean.ocean130,
      pressed: ocean.ocean140,
      disabled: oceanGray.gray60,
    },
    thumb: {
      enabled: ocean.ocean120,
      hover: ocean.ocean130,
      focused: ocean.ocean130,
      pressed: ocean.ocean140,
      disabled: oceanGray.gray60,
    },
    tick: {
      enabled: oceanGray.gray110,
      active: oceanGray.white,
      disabled: oceanGray.gray60,
    },
    valueLabel: {
      bg: oceanGray.gray160,
      text: oceanGray.gray20,
    },
  },
  // Switch 램프 — 라이트. 윤곽(무채)과 ON thumb(유채)이 **채도로** 갈리고, OFF thumb은 무채로
  // 물러나 "채도 = 켜짐 신호"를 잉크에서도 지킨다. 면·잉크 램프가 모두 면 사다리 방향(라이트는
  // 어두워지는 쪽)으로 진행하고, 잉크의 존재감 사다리는 비활성 < 활성 OFF < 활성 ON 순이다
  // (라이트에서 존재감은 어두운 쪽). 왜 밝기로 못 가르는지, 윤곽이 앉을 수 있는 밝기 밴드가
  // 왜 좁은지, 실측 하한은 얼마인지는 RFC-semantic-token-map §`color.switch.*` 가 소유한다 —
  // 값이 바뀌면 그 절만 고치면 되게 여기서는 되풀이하지 않는다.
  // 계약을 지키는 것은 `cssVariables.test.ts` 의 switch 게이트다.
  switch: {
    track: {
      enabled: oceanGray.gray30,
      hover: oceanGray.gray40,
      pressed: oceanGray.gray50,
      // 면 사다리 최하단으로 후퇴. ON(아래 activeTrack.disabled)이 여기서 한 단 앞으로 나온다 —
      // 라이트에서 "앞"은 어두워지는 방향이다(면 사다리가 white에서 gray50으로 내려간다).
      disabled: oceanGray.gray20,
    },
    activeTrack: {
      enabled: ocean.ocean80,
      hover: ocean.ocean90,
      pressed: ocean.ocean100,
      // disabled 인데 유채인 유일한 지점 — 비활성에서도 "꺼짐/켜짐" 이 남는 근거다.
      // 컨테이너 intent 인 primarySubtle 을 빌리지 않는다.
      disabled: ocean.ocean40,
    },
    border: {
      // 무채 축 — 정적 외곽은 유채를 쓰지 않는다는 램프 설계와 같은 방향이다.
      enabled: oceanGray.gray155,
      // border.disabled와 raw 공유 — 비활성 윤곽의 계약은 Switch만의 것이 아니다.
      disabled: oceanGray.gray60,
    },
    thumb: {
      // 무채 축 — OFF 잉크. 윤곽과는 밝기로 갈린다(rest ΔL* 10 이상). 존재감은 ON 잉크(navy)와 같은
      // 급이고 채도만 없다 — 활성 OFF 점은 비활성보다 또렷해야 한다.
      enabled: oceanGray.gray145,
      hover: oceanGray.gray150,
      pressed: oceanGray.gray155,
      // 활성 OFF(gray145)보다 밝은 쪽으로 물러난 Switch 전용 비활성 잉크 — 존재감 사다리
      // (비활성 < 활성)를 잉크에서도 지키기 위해 text.muted raw 공유를 해제했다.
      disabled: oceanGray.gray110,
    },
    activeThumb: {
      // 유채 축 — rest 에서는 윤곽과 밝기·채도 둘 다로 갈리고 pressed 로 갈수록 채도만 남는다.
      enabled: ocean.ocean150,
      hover: ocean.ocean155,
      pressed: ocean.ocean160,
    },
  },
  // default: solid black — 오버레이 알파는 소비 컴포넌트(예: Modal backdrop 애니메이션)가 정한다.
  // overlay: 정적 backdrop dim의 표준값 (RFC-semantic-token-map §"solid vs alpha" — scrim은 alpha 허용군).
  scrim: {
    default: "rgb(0 0 0)",
    overlay: "rgb(0 0 0 / 0.45)",
  },
  // status 코어 — 전 키 램프 파생. success·error·exhausted·checking만 라이트 전용 스텝
  // (다크 블록과 갈림)이고
  // 나머지는 양 테마 같은 스텝을 참조한다. completed는 "완료됨" 표시용 blue(성공
  // 판정 green인 success와 의미 분리),
  // cancelling은 error와 같은 hue의 밝은 단("실패 아님 · 종결 진행 중"),
  // attention은 paused보다 어두운 골드("사용자 조치 필요"),
  // exhausted는 리소스 소진(포화 — 잔여 0) 신호다.
  // 도메인 5그룹(device/job/inference/dataCapture/capacity)은 buildStatus가 이 코어를 별칭해 만든다.
  status: buildStatus(
    {
      success: green.green50,
      running: blue.blue90,
      paused: amber.amber30,
      error: red.red70,
      warning: orange.orange40,
      info: blue.blue60,
      muted: oceanGray.gray130,
      completed: blue.blue70,
      ready: green.green60,
      recording: red.red50,
      cancelling: red.red35,
      attention: amber.amber50,
      exhausted: violet.violet130,
      // checking — 판정/연결 확인 중이며 아직 success·warning·error를 주장하지 않는 상태.
      // text.default와 같은 oceanGray 잉크 스텝이라 라이트/다크가 반전한다.
      checking: oceanGray.gray180,
    },
    [0.2, 0.28, 0.36],
  ),
  // dataViz — 라이트는 밝은 면 위에서 선·틴트가 서도록 각 램프의 중간~진한 단.
  // red/green/blue/violet은 status 코어와 같은 스텝을 공유하고(error·success·running·
  // exhausted 라이트 단), orange·amber는 라이트 면 위 구분을 위해 status fill보다 한둘 진한
  // 단을 쓴다. gray는 muted와 같은 잉크 스텝 — "분류 없음/기타" 자리.
  dataViz: resolveDataVizTokens("light"),
  brand: {
    primary: brandRed,
  },
};

// 다크 사다리는 단조 증가(sunken < canvas < surface < raised < raisedHigh < overlay).
// canvas(gray200 #04060b)·surface(gray170 #0d121c)는 시트 앵커 매핑(In Use 데모의
// page/panel과 동일) — raised 이상은 확장 스텝으로 뚜렷이 밝아져(단차 1.13~1.17)
// 배경↔surface 구분이 안 된다는 대비 리뷰를 해소한다.
export const darkColorTokens: ColorTokens = {
  bg: {
    canvas: oceanGray.gray200,
    surface: oceanGray.gray170,
    surfaceSunken: oceanGray.gray210,
    surfaceRaised: oceanGray.gray160,
    surfaceRaisedHigh: oceanGray.gray155,
    surfaceOverlay: oceanGray.gray150,
    inverse: oceanGray.gray40,
    // base는 gray10(#f9fafb) — surface(#0d121c) 위에서 ≈ #1b2029, raised 위에서도 한 단계 더 밝게.
    muted: alpha(oceanGray.gray10, 0.06),
    // base는 라이트와 같은 앵커 ocean110 — 얹히는 text.default 대비를 selectedActive까지
    // 유지한다. surface(#0d121c) 위에서 ≈ rgb(16 48 63).
    selected: alpha(ocean.ocean110, 0.26),
    // surface(#0d121c) 위에서 ≈ rgb(17 57 73).
    selectedHover: alpha(ocean.ocean110, 0.34),
    // surface(#0d121c) 위에서 ≈ rgb(18 66 84).
    selectedActive: alpha(ocean.ocean110, 0.42),
    attention: alpha(attentionAmber.dark, 0.18),
    attentionHover: alpha(attentionAmber.dark, 0.26),
    attentionActive: alpha(attentionAmber.dark, 0.34),
    // 시트 다크 알림 면(#660708 — red 리저브 최심단) — 위에 onDangerSurface가 11.7:1로 얹힌다.
    dangerSurface: red.red140,
    control: {
      primary: ocean.ocean130,
      primaryHover: ocean.ocean125,
      primaryActive: ocean.ocean120,
      // 다크 컨테이너 톤 — 파생 근거는 ocean 램프 주석.
      primarySubtle: ocean.ocean160,
      // ocean 램프의 한 단 밝은 컨테이너 단.
      primarySubtleHover: ocean.ocean155,
      // 라이트와 같은 이유로 bg 사다리(L* 10.7~32.6) 밖에 둔다 — 그 대역에 겹치면
      // surfaceRaised와 대비가 1.01이 되고 surfaceOverlay(32.6)보다도 어두워 모달 안에서
      // 패널에 뚫린 구멍처럼 보인다. rest L* 37.3에서 primary(48.3) 쪽으로 밝아진다.
      secondary: ocean.ocean145,
      secondaryHover: ocean.ocean140,
      secondaryActive: ocean.ocean137,
      // 다크에서는 높은 상호작용 단계일수록 밝아지는 oceanGray ramp를 따른다.
      neutral: oceanGray.gray155,
      neutralHover: oceanGray.gray150,
      neutralActive: oceanGray.gray145,
      trackingPlay:
        "conic-gradient(#ff5f6dba, #ffc371ba, #ffee55ba, #6be675ba, #4fd0ffba, #4778e2ba, #9a5bffba, #ff6bd6ba, #ff5f6dba)",
      // red/orange fill도 primary와 같은 방향으로 밝아진다(rest < hover < active).
      danger: red.red88,
      dangerHover: red.red85,
      dangerActive: red.red77,
      warning: orange.orange60,
      warningHover: orange.orange50,
      warningActive: orange.orange40,
      disabled: oceanGray.gray155,
      disabledSunken: oceanGray.gray210,
    },
  },
  text: {
    // muted는 시트 다크 예시의 bridge sub-text 단의 재조색(#9cabb7), subtle은 그보다 한 단 낮은
    // 대비 — subtle < muted < default 위계 단조성 유지(surfaceOverlay 위에서도 4.5:1 이상).
    muted: oceanGray.gray80,
    subtle: oceanGray.gray90,
    default: oceanGray.gray30,
    strong: oceanGray.gray10,
    // default의 α0.38 파생.
    disabled: alpha(oceanGray.gray30, 0.38),
    // Keeps primary surface/ghost at AA even on the brightest surfaceOverlay (4.70:1).
    accent: ocean.ocean90,
    // fill rest와 같은 주황 단 — 가장 밝은 면(surfaceOverlay) 위 4.87:1이라 전경으로도 선다.
    warning: orange.orange40,
    // control.danger(red70)보다 두 단 밝은 텍스트 전용 단 — surfaceOverlay 위에서도 4.53:1.
    danger: red.red35,
    inverse: oceanGray.gray180,
    onControl: oceanGray.white,
    // primary와 같은 극성 — red/orange fill 위에서도 흰 잉크가 AA를 지킨다.
    onWarningControl: oceanGray.white,
    onDangerControl: oceanGray.white,
    onDangerSurface: oceanGray.gray20,
    onStatus: oceanGray.white,
  },
  border: {
    // 라이트와 같은 규칙 — 방향만 반대인 흰색 alpha 오버레이다(수치와 소비처는 Storybook
    // `Foundations/Theme/Border Ladder`). 고정색으로는 "각 단을 가장 자주 앉는 면에 맞춘다"는
    // 절충밖에 못 한다 — 면이 6단인데 보더가 면에 종속되지 않으면 한 단이 모든 면에서 동시에
    // 살아남을 수 없고, 그 대가로 어떤 단은 자기 자리 밖 면에서 1.00~1.03까지 내려간다.
    // alpha는 면과 함께 움직이므로 요동이 1.09~1.13배로 눌리고 최소 대비가 1.29를 밑돌지 않는다.
    // rest 3단의 인접 ΔL*는 6.5/8.3이라 1px 선에서 서로 구분된다.
    //
    // 강조 2단: strong이 모든 면에서 3.36~3.82:1이라 다크도 WCAG 1.4.11의 non-text 3:1을
    // 지킨다. 그러면서 default->strong 간격이 rest 단의 1.9배(ΔL* 13.7)에 머물러 rest 묶음과
    // 강조 묶음이 끊기지 않는다 — 한 단 더 올리면 2.6배로 벌어지고, 한 단 내리면 rest 간격과
    // 같아져 정적/상호작용 경계가 사라진다.
    // 상호작용 축(focus/selected*)도 같은 자리에서 모든 면의 3:1을 넘긴다.
    divider: alpha(borderInk.dark, 0.12),
    subtle: alpha(borderInk.dark, 0.18),
    default: alpha(borderInk.dark, 0.26),
    strong: alpha(borderInk.dark, 0.4),
    emphasis: alpha(borderInk.dark, 0.55),
    // 라이트와 같은 규칙, 방향만 반대인 ocean 유채 축. 탈채도 축을 쓰면 다크에서 특히 나쁘다 —
    // 채도가 정적 단과 같은 대역이라 네이비 면과도, 정적 보더와도 갈리지 않는다. 유채 축은
    // surfaceOverlay 위 selected 3.04 / hover 3.52 / active 4.06으로 3:1도 함께 넘긴다.
    focus: ocean.ocean80,
    selected: ocean.ocean115,
    selectedHover: ocean.ocean110,
    selectedActive: ocean.ocean100,
    danger: red.red70,
    // fill 곡선 위 단(#f40026) — dangerSurface 면(#660708) 위 3.05:1.
    dangerSurface: red.red75,
    // disabled는 상태색이 아닌 정적 윤곽이라 유채 상호작용 축을 따라가지 않는다(라이트와
    // 같은 규칙). 라이트 selectedHover가 ocean으로 떠나면서 gray130은 이제 이 토큰과
    // slider disabled만 쓴다.
    disabled: oceanGray.gray130,
  },
  slider: {
    track: {
      enabled: oceanGray.gray150,
      disabled: oceanGray.gray160,
    },
    activeTrack: {
      enabled: ocean.ocean110,
      hover: ocean.ocean100,
      focused: ocean.ocean100,
      pressed: ocean.ocean90,
      disabled: oceanGray.gray130,
    },
    thumb: {
      enabled: ocean.ocean110,
      hover: ocean.ocean100,
      focused: ocean.ocean100,
      pressed: ocean.ocean90,
      disabled: oceanGray.gray130,
    },
    tick: {
      enabled: oceanGray.gray90,
      // Navy stays legible on the bright ocean110–ocean90 indicator ramp.
      active: ocean.ocean160,
      disabled: oceanGray.gray130,
    },
    valueLabel: {
      bg: oceanGray.gray40,
      text: oceanGray.gray180,
    },
  },
  // Switch 램프 — 다크. 라이트와 같은 구조를 극성만 뒤집어 쓴다(면 사다리 방향 = 밝아지는 쪽).
  // 잉크는 white 를 쓰지 않는다 — 밝기 천장에 붙으면 진행 방향이 막힌다. 근거는 라이트 블록과
  // 같은 RFC 절이 소유한다.
  switch: {
    track: {
      enabled: oceanGray.gray160,
      hover: oceanGray.gray155,
      pressed: oceanGray.gray150,
      // 라이트와 같은 "면 사다리 최하단" 규칙. 다크에서 "앞"은 밝아지는 방향이다.
      disabled: oceanGray.gray210,
    },
    activeTrack: {
      // 액션 fill(ocean130~120)보다 한 단 앞(밝음). ON thumb(ocean50)이 이 면 위에서 3:1을
      // 지켜야 하므로 이 이상은 못 밝힌다. 근거·기각 대안은 RFC-semantic-token-map
      // §`color.switch.*`와 §Amendments.
      enabled: ocean.ocean125,
      hover: ocean.ocean120,
      pressed: ocean.ocean115,
      disabled: ocean.ocean160,
    },
    border: {
      enabled: oceanGray.gray30,
      disabled: oceanGray.gray130,
    },
    thumb: {
      enabled: oceanGray.gray60,
      hover: oceanGray.gray50,
      pressed: oceanGray.gray40,
      // 활성 OFF(gray60)보다 어두운 쪽으로 물러난 비활성 잉크 — border.disabled 와 같은
      // "죽은 잉크" raw 다. 다크에서 존재감은 밝은 쪽이다.
      disabled: oceanGray.gray130,
    },
    activeThumb: {
      enabled: ocean.ocean50,
      hover: ocean.ocean40,
      pressed: ocean.ocean30,
    },
  },
  scrim: {
    default: "rgb(0 0 0)",
    overlay: "rgb(0 0 0 / 0.45)",
  },
  // status 코어 — 라이트와 같은 정의에서 success(다크는 채도 정점 green40) ·
  // error(다크 fill 극성에 맞춘 red72) · exhausted(다크 라벤더 violet40) ·
  // checking(oceanGray 잉크)만 스텝이 갈린다.
  // Selected 사다리 알파는 bg.selected*와 같은 이유로 라이트보다 높다.
  status: buildStatus(
    {
      success: green.green40,
      running: blue.blue90,
      paused: amber.amber30,
      error: red.red72,
      warning: orange.orange40,
      info: blue.blue60,
      muted: oceanGray.gray130,
      completed: blue.blue70,
      ready: green.green60,
      recording: red.red50,
      cancelling: red.red35,
      attention: amber.amber50,
      exhausted: violet.violet40,
      checking: oceanGray.gray30,
    },
    [0.26, 0.34, 0.42],
  ),
  // dataViz — 다크는 어두운 면 위에서 갈리도록 red·orange·violet이 밝은 단으로 이동
  // (status 다크의 error/exhausted 분기와 같은 이유). blue·gray는 양 테마에서 같은 단을 쓴다.
  dataViz: resolveDataVizTokens("dark"),
  brand: {
    primary: brandRed,
  },
};

// 라이트: 실제 그림자가 elevation의 주 신호. level은 타이트한 지오메트리 + 농도 상승
// (구 ElevatedSurface 리터럴 `0 2px 4px` 0.1→0.3 계열 계승). overlay/modal 값은 기존
// 컴포넌트 리터럴(dropdown/ColorPicker `0 12px 32px /0.16`, Modal·Toast·AnchoredPopup
// `0 8px 28px /0.18`)을 그대로 승격 — 라이트 시각 변화 없음.
export const lightElevationTokens: ElevationTokens = {
  hairline: "transparent",
  shadow: {
    level: {
      1: "0 2px 4px rgb(0 0 0 / 0.1)",
      2: "0 3px 6px rgb(0 0 0 / 0.16)",
      3: "0 4px 8px rgb(0 0 0 / 0.22)",
      4: "0 6px 12px rgb(0 0 0 / 0.26)",
    },
    overlay: "0 12px 32px rgb(0 0 0 / 0.16)",
    modal: "0 8px 28px rgb(0 0 0 / 0.18)",
  },
};

// 다크: 표면 밝기(bg 사다리)가 주 신호 — 그림자는 검정 위 ambient로만 남고, hairline이
// 가장자리를 정의한다(그림자만으로는 검정 배경에서 경계가 사라지므로).
export const darkElevationTokens: ElevationTokens = {
  hairline: "rgb(255 255 255 / 0.07)",
  shadow: {
    level: {
      1: "0 2px 4px rgb(0 0 0 / 0.35)",
      2: "0 3px 6px rgb(0 0 0 / 0.42)",
      3: "0 4px 8px rgb(0 0 0 / 0.48)",
      4: "0 6px 12px rgb(0 0 0 / 0.55)",
    },
    overlay: "0 12px 32px rgb(0 0 0 / 0.5)",
    modal: "0 8px 28px rgb(0 0 0 / 0.55)",
  },
};
